import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";
import { SITE } from "../data/site";
import { SERVICES } from "../data/site";

const LINKS = [
    { to: "/galerie", label: "Galerie" },
    { to: "/zones", label: "Zones" },
    { to: "/avis", label: "Avis" },
    { to: "/blog", label: "Blog" },
    { to: "/a-propos", label: "À propos" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [svcOpen, setSvcOpen] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); setSvcOpen(false); }, [loc.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
                scrolled || open ? "glass border-b border-linew" : "bg-transparent"
            }`}
            data-testid="navbar"
        >
            <nav className="mx-auto max-w-[1400px] px-5 sm:px-8 h-[76px] flex items-center justify-between gap-6">
                <Link to="/" data-testid="nav-home-link" aria-label="Solino Pro — accueil">
                    <Logo />
                </Link>

                <div className="hidden lg:flex items-center gap-8">
                    <div
                        className="relative"
                        onMouseEnter={() => setSvcOpen(true)}
                        onMouseLeave={() => setSvcOpen(false)}
                    >
                        <button
                            className="text-sm text-ivory/80 hover:text-gold transition-colors uppercase tracking-[0.14em] py-2"
                            data-testid="nav-services-button"
                        >
                            Services ▾
                        </button>
                        <AnimatePresence>
                            {svcOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 8 }}
                                    transition={{ duration: 0.2 }}
                                    className="absolute left-0 top-full w-72 glass border border-linew py-3"
                                    data-testid="nav-services-dropdown"
                                >
                                    {SERVICES.map((s) => (
                                        <Link
                                            key={s.slug}
                                            to={`/${s.slug}`}
                                            className="block px-5 py-2.5 text-sm text-ivory/75 hover:text-gold hover:bg-white/5 transition-colors"
                                            data-testid={`nav-service-${s.slug}`}
                                        >
                                            {s.nav}
                                        </Link>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    {LINKS.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            className={`text-sm uppercase tracking-[0.14em] transition-colors hover:text-gold ${
                                loc.pathname === l.to ? "text-gold" : "text-ivory/80"
                            }`}
                            data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                        >
                            {l.label}
                        </Link>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    <a
                        href={SITE.phoneHref}
                        className="hidden md:flex items-center gap-2 text-sm text-ivory/85 hover:text-gold transition-colors"
                        data-testid="nav-phone-link"
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                        {SITE.phoneDisplay}
                    </a>
                    <Link
                        to="/devis"
                        className="hidden sm:inline-flex items-center rounded-full bg-gold text-ink text-sm font-bold px-6 py-3 hover:scale-[1.04] hover:bg-gold-light transition-transform duration-300"
                        data-testid="nav-cta-devis"
                    >
                        Devis gratuit
                    </Link>
                    <button
                        className="lg:hidden w-11 h-11 flex flex-col items-center justify-center gap-[5px] border border-linew"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Menu"
                        data-testid="nav-burger-button"
                    >
                        <span className={`w-5 h-px bg-ivory transition-transform duration-300 ${open ? "rotate-45 translate-y-[3px]" : ""}`} />
                        <span className={`w-5 h-px bg-ivory transition-transform duration-300 ${open ? "-rotate-45 -translate-y-[3px]" : ""}`} />
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25 }}
                        className="lg:hidden glass border-t border-linew px-6 pb-10 pt-4 max-h-[calc(100vh-76px)] overflow-y-auto"
                        data-testid="nav-mobile-menu"
                    >
                        <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3 mt-4">Services</p>
                        {SERVICES.map((s) => (
                            <Link
                                key={s.slug}
                                to={`/${s.slug}`}
                                className="block py-2.5 text-lg font-display italic text-ivory/85"
                                data-testid={`mobile-service-${s.slug}`}
                            >
                                {s.nav}
                            </Link>
                        ))}
                        <div className="gold-rule my-5" />
                        {LINKS.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                className="block py-3 text-base uppercase tracking-[0.14em] text-ivory/85"
                                data-testid={`mobile-${l.to.replace("/", "")}`}
                            >
                                {l.label}
                            </Link>
                        ))}
                        <Link
                            to="/devis"
                            className="mt-6 flex items-center justify-center rounded-full bg-gold text-ink font-bold px-6 py-4"
                            data-testid="mobile-cta-devis"
                        >
                            Devis gratuit en 2 min
                        </Link>
                        <a href={SITE.phoneHref} className="mt-3 flex items-center justify-center gap-2 rounded-full border border-gold/50 text-gold px-6 py-4" data-testid="mobile-cta-call">
                            Appeler le {SITE.phoneDisplay}
                        </a>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
